import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import TrainerLayout from './TrainerLayout';

const KURS_FARBEN = {
  pirat:        { bg: '#e3f2fd', color: '#1565c0', label: '🏴‍☠️ Pirat' },
  seepferdchen: { bg: '#e8f5e9', color: '#2e7d32', label: '🐴 Seepferdchen' },
  trixi:        { bg: '#fff8e1', color: '#f57f17', label: '🌟 Trixi' },
  bronze:       { bg: '#fbe9e7', color: '#bf360c', label: '🥉 Bronze' },
  silber:       { bg: '#f3e5f5', color: '#6a1b9a', label: '🥈 Silber' },
  gold:         { bg: '#fff9c4', color: '#f9a825', label: '🥇 Gold' },
  hai:          { bg: '#e8eaf6', color: '#1a237e', label: '🦈 Hai' },
  la_silber:    { bg: '#eceff1', color: '#37474f', label: '🦈 LA Silber' },
  la_gold:      { bg: '#fff8e1', color: '#e65100', label: '🦈 LA Gold' },
};

// DB-Schema: 0=Montag…6=Sonntag; JS getDay(): 0=Sonntag, 1=Montag…6=Samstag
const WOCHENTAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function jsTagZuDbTag(jsTag) {
  return jsTag === 0 ? 6 : jsTag - 1;
}

const terminText = (t) =>
  `${WOCHENTAGE_KURZ[t.wochentag]} ${t.uhrzeit_von?.slice(0, 5)}${t.uhrzeit_bis ? `–${t.uhrzeit_bis.slice(0, 5)}` : ''}`;

function heutigeTermine(termine) {
  const dbTag = jsTagZuDbTag(new Date().getDay());
  return (termine || []).filter(t => t.wochentag === dbTag);
}

export default function TrainerGruppen() {
  const navigate = useNavigate();
  const [gruppen, setGruppen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/trainer/gruppen')
      .then(r => setGruppen(r.data))
      .catch(() => setError('Gruppen konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <TrainerLayout title="Meine Gruppen">
      <button onClick={() => navigate('/trainer/stunden')}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '0.7rem',
          marginBottom: '0.75rem', fontWeight: 700, fontSize: '0.95rem', color: '#1565c0', cursor: 'pointer',
        }}>
        ⏱ Meine Stunden
      </button>
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      {!loading && !error && gruppen.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
          Keine Gruppen vorhanden
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {gruppen.map(g => {
          const farbe = KURS_FARBEN[g.kurs_typ] || { bg: '#f5f5f5', color: '#333', label: g.kurs_typ };
          const heute = heutigeTermine(g.termine);
          const heuteErfasst = !!g.heutige_einheit_id;

          return (
            <button key={g.id}
              onClick={() => navigate(`/trainer/gruppen/${g.id}`)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12,
                padding: '1rem', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                position: 'relative',
              }}>
              {heuteErfasst && (
                <div style={{
                  position: 'absolute', top: 10, right: 10,
                  background: '#4caf50', color: '#fff', borderRadius: 20,
                  padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700,
                }}>✓ Heute erfasst</div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem' }}>
                <span style={{
                  background: farbe.bg, color: farbe.color,
                  borderRadius: 8, padding: '2px 10px', fontSize: '0.8rem', fontWeight: 700,
                }}>{farbe.label}</span>
              </div>

              <div style={{ fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.25rem' }}>{g.name}</div>

              <div style={{ fontSize: '0.82rem', color: '#555', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                {g.trainer_name && <span>👤 {g.trainer_name}</span>}
                <span>👥 {g.aktive_mitglieder || 0} Schwimmer</span>
                {g.antraege_ueberfaellig > 0 && (
                  <span style={{ color: '#fff', background: '#c62828', borderRadius: 8, padding: '2px 8px', fontWeight: 700, alignSelf: 'flex-start' }}>
                    ⚠ {g.antraege_ueberfaellig} Aufnahmeantrag{g.antraege_ueberfaellig === 1 ? '' : 'e'} überfällig
                  </span>
                )}
                {/* Trainingstage immer anzeigen – mehrere Gruppen heißen ähnlich */}
                {(g.termine || []).length > 0 ? (
                  <span style={{ color: '#555' }}>
                    📅 {g.termine.map(terminText).join(' · ')} Uhr
                  </span>
                ) : (
                  <span style={{ color: '#999' }}>📅 Kein Trainingstermin hinterlegt</span>
                )}
                {heute.length > 0 && (
                  <span style={{ color: '#1565c0', fontWeight: 700 }}>
                    ▶ Heute Training: {heute.map(t => `${t.uhrzeit_von?.slice(0,5)}${t.uhrzeit_bis ? `–${t.uhrzeit_bis.slice(0,5)}` : ''}`).join(', ')} Uhr
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </TrainerLayout>
  );
}
